<main id="main-content" class="h-screen flex flex-col pl-10 py-4 pr-4 transition-all duration-0">
    <div class="w-full flex flex-col p-2 items-center gap-2">
        <div class="w-full flex flex-row items-center justify-between">
            <div class="w-full flex gap-2 items-center justify-start">
                <h1 class="oswald-font text-3xl uppercase">Emergency Utilities Checklist</h1>
                <div data-popover-target="popover-bottom" data-popover-placement="bottom" type="button">
                    <lucide-icon [name]="icons.CircleHelp" [size]=24 class="text-red-500"></lucide-icon>
                </div>
                <div data-popover id="popover-bottom" role="tooltip"
                    class="absolute z-30 invisible inline-block w-fit max-w-[600px] text-sm text-gray-500 transition-opacity duration-300 bg-white border border-gray-200 rounded-md shadow-xs opacity-0">
                    <div class="px-3 py-2">
                        <p>
                            Record the location and responsibility for each utility, list providers/suppliers, then click
                            <span class="inline-flex px-3 py-1 items-center justify-center rounded-md bg-gradient-to-b from-nside-orange-300 to-nside-orange-400 text-white oswald-font">Save</span>.
                        </p>
                    </div>
                    <div data-popper-arrow></div>
                </div>
            </div>
        </div>
    </div>

    <!-- MAIN CONTENT -->
    <div class="p-2 w-full flex flex-grow min-h-0 overflow-y-auto">
        <form #checklistForm="ngForm" (ngSubmit)="onSubmit()"
            class="h-full w-full bg-white rounded-md shadow-custom-2 flex flex-col p-6 gap-4 overflow-hidden">

            <div class="flex-1 min-h-0 w-full overflow-y-auto flex flex-col gap-8 pr-1">

                <!-- Utilities checklist -->
                <div class="w-full flex flex-col gap-2">
                    <h3 class="oswald-font text-2xl text-nside-orange-400">Utilities</h3>
                    <table class="w-full text-left table-fixed">
                        <thead class="oswald-font text-gray-700 uppercase bg-main-background">
                            <tr>
                                <th class="px-3 py-2 w-1/3">Utility</th>
                                <th class="px-3 py-2">Location</th>
                                <th class="px-3 py-2">Responsibility</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr *ngFor="let row of utilityRows" class="border-b align-top">
                                <th scope="row" class="px-3 py-3 font-medium align-top"><span class="break-words [overflow-wrap:anywhere]">{{ row.label }}</span></th>
                                <td class="px-3 py-3 align-top">
                                    <input *ngIf="canSave()" type="text" [name]="row.location" [(ngModel)]="data[row.location]"
                                        class="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded p-2" placeholder="Location">
                                    <p *ngIf="!canSave()" class="w-full p-2 text-sm whitespace-pre-line break-words"
                                        [ngClass]="data[row.location] ? 'text-gray-900' : 'text-gray-400 italic'">{{ data[row.location] || 'Empty' }}</p>
                                </td>
                                <td class="px-3 py-3 align-top">
                                    <input *ngIf="canSave()" type="text" [name]="row.responsibility" [(ngModel)]="data[row.responsibility]"
                                        class="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded p-2" placeholder="Responsibility">
                                    <p *ngIf="!canSave()" class="w-full p-2 text-sm whitespace-pre-line break-words"
                                        [ngClass]="data[row.responsibility] ? 'text-gray-900' : 'text-gray-400 italic'">{{ data[row.responsibility] || 'Empty' }}</p>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Providers / Suppliers -->
                <div class="w-full flex flex-col gap-2">
                    <h3 class="oswald-font text-2xl text-nside-orange-400">Providers/Suppliers</h3>
                    <div class="flex flex-col px-1 divide-y divide-gray-200">
                        <div *ngFor="let row of providerRows" class="flex flex-row items-center gap-4 py-2.5">
                            <span class="w-40 shrink-0 font-medium text-gray-900">{{ row.label }}</span>
                            <input *ngIf="canSave()" type="text" [name]="row.key" [(ngModel)]="data[row.key]"
                                class="flex-1 min-w-0 bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded p-2" placeholder="Providers/Suppliers">
                            <p *ngIf="!canSave()" class="flex-1 min-w-0 p-2 text-sm whitespace-pre-line break-words"
                                [ngClass]="data[row.key] ? 'text-gray-900' : 'text-gray-400 italic'">{{ data[row.key] || 'Empty' }}</p>
                        </div>
                    </div>
                </div>

                <!-- Yes / No question -->
                <div class="w-full flex flex-row items-center gap-4">
                    <span class="flex-1 min-w-0 font-medium text-gray-900">Do your custodians know where the Master Valves/Switches/Shut-offs are located?</span>
                    <div *ngIf="canSave()" class="shrink-0 flex flex-row items-center gap-3">
                        <button type="button" (click)="selectYesNo('Yes')"
                            class="inline-flex items-center justify-center w-16 px-3 py-1 text-sm font-medium text-center bg-gray-50 border rounded hover:bg-gray-100 cursor-pointer"
                            [ngClass]="{
                                'border-nside-orange-400 text-nside-orange-400': data.custodians_know_locations === 'Yes',
                                'border-gray-300 text-gray-500': data.custodians_know_locations !== 'Yes'
                            }">Yes</button>
                        <button type="button" (click)="selectYesNo('No')"
                            class="inline-flex items-center justify-center w-16 px-3 py-1 text-sm font-medium text-center bg-gray-50 border rounded hover:bg-gray-100 cursor-pointer"
                            [ngClass]="{
                                'border-nside-orange-400 text-nside-orange-400': data.custodians_know_locations === 'No',
                                'border-gray-300 text-gray-500': data.custodians_know_locations !== 'No'
                            }">No</button>
                    </div>
                    <p *ngIf="!canSave()" class="shrink-0 w-16 p-2 text-sm"
                        [ngClass]="(data.custodians_know_locations === 'Yes' || data.custodians_know_locations === 'No') ? 'text-gray-900' : 'text-gray-400 italic'">
                        {{ (data.custodians_know_locations === 'Yes' || data.custodians_know_locations === 'No') ? data.custodians_know_locations : 'Empty' }}
                    </p>
                </div>
            </div>

            <!-- Footer -->
            <div class="w-full shrink-0 flex flex-row gap-2 items-center justify-between">
                <div *ngIf="data.last_updated_at" class="flex flex-col items-start">
                    <p class="oswald-font font-normal">Last Updated At: <span class="raleway-font">{{ data.last_updated_at | date: 'MM/dd/yyyy - HH:mm' }}</span></p>
                    <p class="oswald-font font-normal">By: <span class="raleway-font">{{ data.last_updated_user_name || 'None' }}</span></p>
                </div>
                <button *ngIf="canSave()" type="submit"
                    class="w-20 flex items-center justify-center px-4 py-1.5 rounded-md border bg-gradient-to-b oswald-font text-white ml-auto border-nside-orange-100 from-nside-orange-300 to-nside-orange-400 hover:bg-gradient-to-b hover:from-nside-orange-100 hover:to-nside-orange-200">
                    Save
                </button>
            </div>
        </form>
    </div>

    <prv-next-buttons></prv-next-buttons>
</main>

<introduction></introduction>

<popup-message [type]="messageModal" [message]="popupMessage" (close)="resetMessageModal()"></popup-message>

<div *ngIf="loading"><spinner></spinner></div>
